/* eslint-disable consistent-return */

'use client';

import type { ElementType, RefObject } from 'react';
import { useEffect } from 'react';
import type { AriaButtonOptions, HoverEvents } from 'react-aria';
import {
  mergeProps,
  useButton as useReactAriaButton,
  useFocusRing,
  useHover,
} from 'react-aria';
import { useSharedHoverContext } from '../SharedHover';

export type UseButtonProps = AriaButtonOptions<ElementType> &
  HoverEvents & {
    enableSharedHover?: boolean;
    /**
     * Enable an undocumented react-aria prop for disabling
     * focus on the button element when it's pressed.
     * https://github.com/adobe/react-spectrum/blob/60e30029e5627ac514a354219a6f790f5fee45f7/packages/%40react-aria/button/src/useButton.ts#L58
     */
    preventFocusOnPress?: boolean;
  };

export function useButton(
  ref: RefObject<HTMLButtonElement | null>,
  { enableSharedHover: enableSharedHoverProp, ...props }: UseButtonProps
) {
  /**
   * React Area Button props, including pressed states.
   */
  const { buttonProps, isPressed } = useReactAriaButton(
    {
      ...props,
      onPressStart: (e) => {
        props.onPressStart?.(e);
        // evaluate the pointerType property to call the appropriate pointer type processing function
        if (['mouse', 'touch'].includes(e.pointerType)) {
          // @ts-expect-error - React aria deliberately doesn't support mousedown
          // events, but we need to support them for legacy popover and
          // tooltip support.
          props.onMouseDown?.(new MouseEvent('mousedown', e));
        }
      },
    },
    ref
  );

  const { focusProps, isFocused, isFocusVisible } = useFocusRing(props);

  /**
   * Hover states:
   * Support shared hover when buttons are of type `tertiary` and a `SharedHover`
   * context is provided.
   */

  const {
    onEnterTarget,
    onLeaveTarget,
    onLockTarget,
    onUnlockTarget,
    enabled: hasSharedHoverContext,
  } = useSharedHoverContext();

  const enableSharedHover = hasSharedHoverContext && enableSharedHoverProp;

  const { hoverProps, isHovered } = useHover(
    mergeProps(
      props,
      enableSharedHover && {
        onHoverStart: () => {
          onEnterTarget(ref.current.getBoundingClientRect());
        },
        onHoverEnd: () => {
          onLeaveTarget();
        },
      }
    )
  );

  const lockHover = props['aria-expanded'] && enableSharedHover;

  useEffect(() => {
    if (lockHover) {
      const bounds = ref.current.getBoundingClientRect();
      onLockTarget(bounds);
      return () => {
        onUnlockTarget(bounds);
      };
    }
  }, [ref, onLockTarget, onUnlockTarget, lockHover]);

  return {
    hasSharedHover: enableSharedHover,
    buttonProps: {
      'data-disabled': props.isDisabled || undefined,
      'data-pressed': isPressed || undefined,
      'data-focused': isFocused || undefined,
      'data-hovered': isHovered || undefined,
      'data-focus-visible': isFocusVisible || undefined,
      ...mergeProps(buttonProps, focusProps, hoverProps),
    },
  };
}
